import { Capacitor } from '@capacitor/core';

const YOUTUBE_ID = /^[\w-]{6,}$/;

/** Public web origin that hosts `/youtube-embed.html` (real HTTPS Referer). */
export function youtubeEmbedHost(apiUrl = import.meta.env.VITE_API_URL || ''): string {
  if (apiUrl.includes('iron-forums-app-staging') || apiUrl.includes('iron-forums-staging')) {
    return 'https://iron-forums-staging.up.railway.app';
  }
  return 'https://app.ironforums.org';
}

export function getYouTubeId(url?: string): string | null {
  if (!url) return null;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  const host = parsed.hostname.replace(/^www\.|^m\./, '');
  let id: string | null = null;
  if (host === 'youtu.be') {
    id = parsed.pathname.split('/')[1] || null;
  } else if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
    if (parsed.pathname === '/watch') {
      id = parsed.searchParams.get('v');
    } else {
      const match = parsed.pathname.match(/^\/(?:embed|shorts|live|v)\/([^/?#]+)/);
      id = match ? match[1] : null;
    }
  }
  return id && YOUTUBE_ID.test(id) ? id : null;
}

export function youtubeWatchUrl(videoId: string): string {
  return `https://www.youtube.com/watch?v=${encodeURIComponent(videoId)}`;
}

/**
 * YouTube Error 153: the player rejects Capacitor origins (`capacitor://localhost`,
 * `https://localhost`) because they send no acceptable Referer. Native loads a
 * wrapper page on our HTTPS host so YouTube sees `app.ironforums.org`.
 */
/** Native apps and iPhone/iPad Safari need the HTTPS wrapper. A direct embed stays black (Error 153). */
export function needsHostedYouTubeEmbed(native = Capacitor.isNativePlatform()): boolean {
  if (native) return true;
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /iPhone|iPad|iPod/.test(ua)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

export function youtubeEmbedSrc(
  videoId: string,
  native = Capacitor.isNativePlatform(),
): string {
  if (!YOUTUBE_ID.test(videoId)) return '';
  if (needsHostedYouTubeEmbed(native)) {
    return `${youtubeEmbedHost()}/youtube-embed.html?v=${encodeURIComponent(videoId)}`;
  }
  return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&playsinline=1`;
}
